import React, { useMemo } from 'react'

interface AppWaveformVisualizerProps {
  mode?: 'progress' | 'pulse'
  progress?: number
  isActive?: boolean
  segmentHeights?: number[]
  segmentCount?: number
  randomizeHeights?: boolean
  minHeight?: number
  maxHeight?: number
  activeColorClass?: string
  inactiveColorClass?: string
  className?: string
  segmentWidthClass?: string
  segmentGap?: string
}

const DEFAULT_SEGMENT_HEIGHTS = [
  6, 12, 9, 16, 8, 13, 10, 18, 7, 15, 9, 14, 8, 17, 10, 13, 7, 16, 11, 14, 8, 12, 9, 15, 7, 13, 9,
  16, 10, 12,
]

export const AppWaveformVisualizer: React.FC<AppWaveformVisualizerProps> = ({
  mode = 'progress',
  progress = 0,
  isActive = false,
  segmentHeights,
  segmentCount,
  randomizeHeights = false,
  minHeight = 6,
  maxHeight = 18,
  activeColorClass = 'bg-primary-appBrand',
  inactiveColorClass = 'bg-app-grey-20',
  className = '',
  segmentWidthClass = 'w-[2px]',
  segmentGap = 'gap-[1px]',
}) => {
  const safeProgress = Math.min(Math.max(progress, 0), 1)
  const heights = useMemo(() => {
    if (segmentHeights?.length) {
      return segmentHeights
    }
    const count = segmentCount || DEFAULT_SEGMENT_HEIGHTS.length
    if (!randomizeHeights) {
      return Array.from(
        { length: count },
        (_, idx) => DEFAULT_SEGMENT_HEIGHTS[idx % DEFAULT_SEGMENT_HEIGHTS.length]
      )
    }
    return Array.from(
      { length: count },
      () => Math.floor(Math.random() * (maxHeight - minHeight + 1)) + minHeight
    )
  }, [segmentHeights, segmentCount, randomizeHeights, minHeight, maxHeight])

  return (
    <div className={`flex items-center ${segmentGap} flex-1 px-2 ${className}`}>
      {heights.map((height, index) => {
        const ratio = (index + 1) / heights.length
        const shouldHighlight = mode === 'progress' ? ratio <= safeProgress : isActive

        return (
          <span
            key={`${height}-${index}`}
            className={`${segmentWidthClass} rounded-full transition-all duration-300 ease-out ${
              shouldHighlight ? activeColorClass : inactiveColorClass
            } ${mode === 'pulse' && isActive ? 'animate-pulse' : ''}`}
            style={{
              height: `${height}px`,
              animationDelay: mode === 'pulse' ? `${index * 0.05}s` : undefined,
            }}
          />
        )
      })}
    </div>
  )
}
